import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import {
  BarChart3, TrendingUp, FileDown, Calendar, Users, Car,
  Package, AlertTriangle, Download, RefreshCw, Filter,
  Eye, Target, Activity, Clock, Award, Briefcase
} from 'lucide-react';
import axios from 'axios';
import {
  MonthlyAnalytics,
  EmployeeAnalytics,
  VehicleAnalytics,
  InventoryAnalytics,
  ExportCenter
} from './AnalyticsComponents.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL;

// Overview Dashboard Component
const OverviewDashboard = ({ data }) => {
  if (!data) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Aktive Mitarbeiter</p>
                <p className="text-2xl font-bold text-blue-600">{data.totals.employees}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Teams</p>
                <p className="text-2xl font-bold text-green-600">{data.totals.teams}</p>
              </div>
              <Briefcase className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Fahrzeuge</p>
                <p className="text-2xl font-bold text-purple-600">{data.totals.vehicles}</p>
              </div>
              <Car className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Inventarartikel</p>
                <p className="text-2xl font-bold text-orange-600">{data.totals.inventory_items}</p>
              </div>
              <Package className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Current Month Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-500" />
              Aktueller Monat
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <div>
                <p className="font-medium">Aufträge</p>
                <p className="text-sm text-slate-600">Alle Assignments</p>
              </div>
              <div className="text-2xl font-bold text-emerald-600">
                {data.current_month.assignments}
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div>
                <p className="font-medium">Genehmigte Urlaube</p>
                <p className="text-sm text-slate-600">Bewilligte Anträge</p>
              </div>
              <div className="text-2xl font-bold text-blue-600">
                {data.current_month.approved_vacations}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Aktuelle Warnungen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <div>
                <p className="font-medium">Niedrige Lagerbestände</p>
                <p className="text-sm text-slate-600">Artikel unter Mindestbestand</p>
              </div>
              <div className="text-2xl font-bold text-red-600">
                {data.alerts.low_stock_items}
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <div>
                <p className="font-medium">Wartung fällig</p>
                <p className="text-sm text-slate-600">Fahrzeuge in 30 Tagen</p>
              </div>
              <div className="text-2xl font-bold text-yellow-600">
                {data.alerts.vehicles_maintenance_due}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const Analysis = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [overviewData, setOverviewData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [vehicleData, setVehicleData] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  
  // Date filters
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0].slice(0, 7) + '-01');
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  // Load data functions
  const loadOverviewData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverviewData(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Übersichtsdaten:', error);
    }
  };

  const loadMonthlyData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/analytics/monthly`, {
        params: { year: selectedYear, month: selectedMonth },
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonthlyData(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Monatsdaten:', error);
    }
  };

  const loadEmployeeData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/analytics/employees`, {
        params: { start_date: startDate, end_date: endDate },
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeData(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Mitarbeiterdaten:', error);
    }
  };

  const loadVehicleData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/analytics/vehicles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicleData(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Fahrzeugdaten:', error);
    }
  };

  const loadInventoryData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/analytics/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventoryData(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Inventardaten:', error);
    }
  };

  const refreshAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadOverviewData(),
        loadMonthlyData(),
        loadEmployeeData(),
        loadVehicleData(),
        loadInventoryData()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = async (type) => {
    try {
      const token = localStorage.getItem('authToken');
      let url = '';
      let filename = '';

      if (type === 'monthly') {
        url = `${BACKEND_URL}/api/analytics/export/monthly-csv?year=${selectedYear}&month=${selectedMonth}`;
        filename = `monthly_analytics_${selectedYear}_${selectedMonth.toString().padStart(2, '0')}.csv`;
      } else if (type === 'employees') {
        url = `${BACKEND_URL}/api/analytics/export/employees-csv?start_date=${startDate}&end_date=${endDate}`;
        filename = `employee_analytics_${startDate}_${endDate}.csv`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      
      // Cleanup
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      alert('Fehler beim Export: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    }
  };

  useEffect(() => {
    refreshAllData();
  }, [selectedYear, selectedMonth, startDate, endDate]);

  if (loading && !overviewData) {
    return (
      <div className="min-h-full bg-slate-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <BarChart3 className="w-12 h-12 text-emerald-600 mx-auto animate-pulse" />
          <p className="text-slate-600 mt-2">Lädt Analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50 p-6" data-testid="analysis-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-emerald-600" />
              Analytics Dashboard 📊
            </h1>
            <p className="text-slate-600 mt-1">Umfassende Analysen und Berichte für alle Geschäftsbereiche</p>
          </div>
          
          <div className="flex items-center gap-2 mt-4 md:mt-0">
            <Button
              onClick={refreshAllData}
              disabled={loading}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        {/* Tab Navigation */}
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Übersicht
          </TabsTrigger>
          <TabsTrigger value="monthly" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Monatlich
          </TabsTrigger>
          <TabsTrigger value="employees" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Mitarbeiter
          </TabsTrigger>
          <TabsTrigger value="vehicles" className="flex items-center gap-2">
            <Car className="w-4 h-4" />
            Fahrzeuge
          </TabsTrigger>
          <TabsTrigger value="inventory" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Inventar
          </TabsTrigger>
          <TabsTrigger value="exports" className="flex items-center gap-2">
            <FileDown className="w-4 h-4" />
            Export
          </TabsTrigger>
        </TabsList>

        {/* Overview Dashboard */}
        <TabsContent value="overview">
          <OverviewDashboard data={overviewData} />
        </TabsContent>

        {/* Monthly Analytics */}
        <TabsContent value="monthly">
          <MonthlyAnalytics 
            data={monthlyData}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            onYearChange={setSelectedYear}
            onMonthChange={setSelectedMonth}
          />
        </TabsContent>

        {/* Employee Analytics */}
        <TabsContent value="employees">
          <EmployeeAnalytics
            data={employeeData}
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
        </TabsContent>

        {/* Vehicle Analytics */}
        <TabsContent value="vehicles">
          <VehicleAnalytics data={vehicleData} />
        </TabsContent>

        {/* Inventory Analytics */}
        <TabsContent value="inventory">
          <InventoryAnalytics data={inventoryData} />
        </TabsContent>

        {/* Export Center */}
        <TabsContent value="exports">
          <ExportCenter 
            onExport={exportCSV}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            startDate={startDate}
            endDate={endDate}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Analysis;